import useOrderCustomFields from '@/api/mutations/custom-field-groups/useOrderCustomFields';
import meKeys from '@/api/queries/me/keyFactory';
import {
  MyCustomFieldGroupsQuery,
  OrderCustomFieldsInput,
  OrderCustomFieldsMutation,
} from '@/gql/graphql';
import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

const useOptimisticOrderCustomFields = (
  options?: UseMutationOptions<
    OrderCustomFieldsMutation,
    unknown,
    OrderCustomFieldsInput
  >
) => {
  const queryClient = useQueryClient();

  return useOrderCustomFields({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: meKeys.customFieldGroups(),
      });

      // Snapshot the previous value
      const previousCustomFieldGroups =
        queryClient.getQueryData<MyCustomFieldGroupsQuery>(
          meKeys.customFieldGroups()
        );

      // Optimistically update the cache
      queryClient.setQueryData<MyCustomFieldGroupsQuery>(
        meKeys.customFieldGroups(),
        produce<MyCustomFieldGroupsQuery>((draft) => {
          if (!draft) return;

          const group = draft.me.user.account.customFieldGroups.find(
            (g) => g.id === variables.groupId
          );

          if (!group) return;

          // Reorder custom fields based on orderedCustomFieldIds
          const orderedFields = variables.orderedCustomFieldIds
            .map((id) => group.customFields.find((f) => f.id === id))
            .filter((f): f is NonNullable<typeof f> => f !== undefined);

          // Keep any fields not in the ordered list at the end
          const remainingFields = group.customFields.filter(
            (f) => !variables.orderedCustomFieldIds.includes(f.id)
          );

          group.customFields = [...orderedFields, ...remainingFields];
        })
      );

      options?.onMutate?.(variables);

      return { previousCustomFieldGroups };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      const previousCustomFieldGroups = (
        context as { previousCustomFieldGroups?: MyCustomFieldGroupsQuery }
      )?.previousCustomFieldGroups;

      if (previousCustomFieldGroups) {
        queryClient.setQueryData(
          meKeys.customFieldGroups(),
          previousCustomFieldGroups
        );
      }

      options?.onError?.(err, variables, context);
    },
    onSettled: (...args) => {
      // Invalidate to refetch from server (source of truth)
      void queryClient.invalidateQueries({
        queryKey: meKeys.customFieldGroups(),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticOrderCustomFields;
